import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Modal, ModalHeader } from '#components/patterns/modal/index.ts';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import { useTime } from '#hooks/useTime.ts';
import { generateFilename } from '#util/generateFilename.ts';
import type { FormData } from '../../form-types.ts';
import { DownloadLogs } from '../download/DownloadLogs.tsx';
import { ExportLogsForm } from '../form/ExportLogsForm.tsx';
import { useGetAuditLogsBlobURL } from './ExportLogsModal.queries.tsx';

/**
 * {@link ExportLogsModal} shows a form for selecting which logs you want,
 * a loading state, and a button for downloading your exported logs.
 */
export function ExportLogsModal() {
  const currentTime = useTime();
  const modalRef = useRef(null);
  const navigate = useNavigate();
  const location = useAppLocation();
  const [formData, setFormData] = useState<Partial<FormData>>({
    targetType: undefined,
    targetId: undefined,
    timeFrame: undefined,
  });
  const { isFetching, data: downloadURL } = useGetAuditLogsBlobURL(formData);

  // Free up memory when unmounting
  useEffect(() => {
    if (downloadURL) {
      return () => URL.revokeObjectURL(downloadURL);
    }
  }, [downloadURL]);

  function closeModal() {
    const destination = location.state?.from ?? {
      pathname: '/logs/audit',
      search: location.search,
    };
    void navigate(destination, { replace: true });
  }

  return (
    <Modal
      isOpen={true}
      onClose={() => {
        closeModal();
      }}
      ref={modalRef}
    >
      <ModalHeader>Export CSV</ModalHeader>
      {isFetching || downloadURL ?
        <DownloadLogs
          downloadURL={downloadURL}
          fileName={generateFilename(
            [currentTime.toString(), formData.targetType ?? '', formData.timeFrame ?? '', 'logs'],
            'csv'
          )}
        />
      : <ExportLogsForm
          onSubmit={(data) => {
            setFormData(data);
          }}
          closeModal={closeModal}
        />
      }
    </Modal>
  );
}
